import React, { useCallback } from 'react';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import type { RowProps } from '@amzn/meridian/row/row';
import Tag from '@amzn/meridian/tag';
import { useTranslation } from 'src/arb-wrapper';

interface TagCloudProps extends Omit<RowProps, 'children'> {
  /** Prevents tags from being removed while preserving their visibility. */
  disabled?: boolean;

  /** Array of tags to display */
  tags: string[];

  /** Callback executed when a `Tag`'s close button is clicked. The index of
   * the `Tag` is provided for removal.
   */
  onTagClicked: (index: number) => void;

  /**
   * Callback executed when all tags have been removed with "Clear All" button.
   */
  onClearAllClicked: () => void;
}

/**
 * A controlled component that wraps a Meridian `Row` for displaying a list of
 * removable Meridian `Tag`s.
 */
export const TagCloud: React.FC<TagCloudProps> = (props) => {
  const { disabled = false, tags, onTagClicked, onClearAllClicked, ...rowProps } = props;
  const { t } = useTranslation();

  const handleOnClose = useCallback(
    (index: number) => {
      onTagClicked(index);
    },
    [onTagClicked]
  );

  return (
    <Row wrap="down" {...rowProps}>
      {tags.map((tag, index) => (
        <Tag
          data-testid={`tag-cloud-tag-${index}`}
          key={`tag${index}`}
          onClose={disabled
            ? undefined
            : () => {
                handleOnClose(index);
              }}
        >
          {tag}
        </Tag>
      ))}

      {tags.length > 1 && !disabled && (
        <Button
          data-testid="tag-cloud-clear-all"
          type="link"
          onClick={onClearAllClicked}
        >
          {t('tag_cloud_clear_all')}
        </Button>
      )}
    </Row>
  );
};
